'use client';

import React, { createContext, useContext, useMemo, useReducer, type ReactNode } from 'react';

export type AttentionFlashButtonController = {
    /** Triggers the attention flash animation. */
    trigger: () => void;
    /** Internal value watched by Button.AttentionFlash. Do not read or set directly. */
    triggerKey: number;
};

export type AttentionFlashButtonProviderProps = {
    children: ReactNode;
};

export const AttentionFlashButtonContext = createContext<AttentionFlashButtonController | null>(null);

/** Provides shared attention flash trigger state to descendant buttons and trigger callers. */
export const AttentionFlashButtonProvider: React.FC<AttentionFlashButtonProviderProps> = ({ children }) => {
    const [triggerKey, trigger] = useReducer((currentTriggerKey: number) => currentTriggerKey + 1, 0);
    const controller = useMemo(() => ({ trigger, triggerKey }), [trigger, triggerKey]);

    return <AttentionFlashButtonContext.Provider value={controller}>{children}</AttentionFlashButtonContext.Provider>;
};

/** Returns the nearest attention flash controller from context. */
export const useAttentionFlashButtonController = (): AttentionFlashButtonController => {
    const controller = useContext(AttentionFlashButtonContext);

    if (!controller) {
        throw new Error('Button.AttentionFlash.useController must be used within Button.AttentionFlash.Provider');
    }

    return controller;
};
